<template>
    <div class="table-columns-picker">
        <div class="input-group">
            <span class="input-group-text bg-white border-end-0">
                <i class="fa-regular fa-magnifying-glass"></i>
            </span>
            <input
                v-model.trim="search"
                class="form-control border-start-0"
                type="search"
                :placeholder="__('search')"
                :aria-label="__('search')"
            />
        </div>

        <div class="d-flex align-items-center justify-content-between my-2">
            <span class="text-tiny">{{ __('columns_order') }}</span>

            <span>
                <button
                    type="button"
                    class="btn btn-link p-0 text-tiny text-decoration-none"
                    @click="reset"
                >
                    <i class="fa-solid fa-rotate-left me-1"></i>
                    {{ __('reset_columns') }}
                </button>
                <button
                    type="button"
                    class="btn btn-link p-0 ms-3 text-tiny text-decoration-none"
                    :disabled="hidden.length === 0 && !search"
                    @click="showAll"
                >
                    {{ __('show_all') }}
                </button>
                <button
                    type="button"
                    class="btn btn-link p-0 ms-3 text-tiny text-decoration-none"
                    :disabled="order.length === 0"
                    @click="hideAll"
                >
                    {{ __('hide_all') }}
                </button>
            </span>
        </div>

        <!-- One list, two groups: only the shown columns can be dragged, so a
             drag can never land a column among the hidden ones. -->
        <div
            class="table-columns-picker__list border rounded bg-white overflow-auto custom-scrollbar"
        >
            <draggable
                class="list-group list-group-flush"
                v-model="order"
                handle=".drag-handle"
                :animation="200"
                :disabled="search.length > 0"
                @change="saveOrder"
            >
                <div
                    v-for="key in shown"
                    :key="key"
                    class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-1"
                >
                    <i class="fa-solid fa-grip-vertical drag-handle text-secondary"></i>
                    <i :class="icon(key)" class="fa-fw text-secondary"></i>
                    <span class="me-auto">{{ label(key) }}</span>
                    <toggle-button :value="true" :sync="true" @change="toggle(key)" />
                </div>
            </draggable>

            <div class="list-group list-group-flush">
                <div
                    v-for="key in hidden"
                    :key="key"
                    class="list-group-item list-group-item-action d-flex align-items-center gap-2 py-1 text-muted"
                >
                    <!-- Same grip, invisible: keeps the labels lined up -->
                    <i class="fa-solid fa-grip-vertical invisible"></i>
                    <i :class="icon(key)" class="fa-fw"></i>
                    <span class="me-auto">{{ label(key) }}</span>
                    <toggle-button :value="false" :sync="true" @change="toggle(key)" />
                </div>

                <div
                    v-if="shown.length === 0 && hidden.length === 0"
                    class="list-group-item text-center text-muted text-tiny"
                >
                    {{ __('empty_search') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import { ToggleButton } from 'vue-js-toggle-button'
import { useTruckColumns } from '@/composables/useTruckColumns'

/**
 * Which columns the vehicles table shows, and in which order.
 *
 * Every row of the catalogue is listed once — the picked ones first, draggable,
 * then the rest — with a toggle to move a column between the two groups. There
 * are no props and no events: the selection lives in the `vehicleListTableFields`
 * user config, which `useTruckColumns()` reads and writes, and the table reads
 * from the same place.
 */
export default {
    name: 'TableColumnsPicker',
    components: {
        draggable,
        ToggleButton
    },
    setup() {
        return useTruckColumns()
    }
}
</script>

<style scoped>
/* The list is the only part of the settings worth giving room to, so it takes
   whatever height the modal has left over (see `VehicleListSettings`). */
.table-columns-picker {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
}

/* Never shorter than a few rows: a search that matches one column should not
   collapse the list, and neither should a short window. */
.table-columns-picker__list {
    flex: 1 1 auto;
    min-height: 240px;
}

/* No Bootstrap utility for this one, and `draggable` needs the class anyway */
.drag-handle {
    cursor: grab;
}
</style>
